import { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import CampaignItem from './CampaignItem.jsx';
import BroadcastItem from './BroadcastItem.jsx';
import { getEffectiveDisplayDate } from './helpers.js';
import styles from './CalendarView.module.css';

export default function MonthCard({
  month,
  monthIndex,
  year,
  campaigns,
  broadcasts = [],
  isCurrentMonth,
  isPastMonth,
  zIndex,
  userType,
  hoveredCampaign,
  onCampaignHover,
  onCampaignClick,
  onBroadcastClick,
  onDateClick,
  onArchiveModalChange,
  onMonthClick,
  isModalOrDropdownClick,
}) {
  const { t } = useTranslation('campaigns');
  const [hoveredBroadcast, setHoveredBroadcast] = useState(null);
  const listRef = useRef(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);

  useEffect(() => {
    const checkScrollable = () => {
      if (listRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = listRef.current;
        const isScrollable = scrollHeight > clientHeight;
        const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 1;
        setHasMoreContent(isScrollable && isNotAtBottom);
      }
    };

    checkScrollable();

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', checkScrollable);
      return () => listElement.removeEventListener('scroll', checkScrollable);
    }
  }, [campaigns, broadcasts]);

  // Mark campaigns with itemType for sorting
  const campaignsWithType = campaigns.map(c => ({
    ...c,
    itemType: 'campaign',
    sortDate: getEffectiveDisplayDate(c, monthIndex, year) || '',
  }));

  // Mark broadcasts with itemType for sorting
  const broadcastsWithType = broadcasts.map(b => ({
    ...b,
    itemType: 'broadcast',
    sortDate: b.displayDate || '',
  }));

  // Combine and sort all items by date
  const allItems = [...campaignsWithType, ...broadcastsWithType].sort((a, b) =>
    (a.sortDate || '').localeCompare(b.sortDate || ''),
  );

  const handleBroadcastHover = (uuid, isHovered) => {
    setHoveredBroadcast(isHovered ? uuid : null);
  };

  return (
    <Box
      className={`${styles.monthCard} ${
        isCurrentMonth ? styles.monthCardCurrent : ''
      } ${isPastMonth ? styles.monthCardPast : ''}`}
      style={{ zIndex }}
      role="region"
      aria-label={`${month} ${year}`}
      tabIndex={0}
    >
      <Box className={styles.monthHeader}>
        <Text
          className={`${styles.monthTitleText} ${styles.monthTitleClickable}`}
          size="5"
          weight="medium"
          onClick={() => onMonthClick && onMonthClick({ month, monthIndex })}
        >
          {month}
        </Text>
        <Flex className={styles.monthMeta} align="center">
          <Text size="1" className={styles.monthMetaText}>
            {year}
            {isCurrentMonth && ` • ${t('calendar.currentMonth')}`}
            {isPastMonth && ` • ${t('calendar.completed')}`}
          </Text>
        </Flex>
      </Box>
      <Box className={styles.dottedSeparator} />
      <Box
        className={`${styles.campaignListWrapper} ${
          hasMoreContent ? styles.campaignListHasMore : ''
        }`}
      >
        <Flex
          ref={listRef}
          className={styles.campaignList}
          direction="column"
          as="ul"
        >
          {allItems.map(item =>
            item.itemType === 'broadcast' ? (
              <BroadcastItem
                key={`broadcast-${item.uuid}`}
                broadcast={item}
                hoveredBroadcast={hoveredBroadcast}
                onHover={handleBroadcastHover}
                onClick={onBroadcastClick}
                isModalOrDropdownClick={isModalOrDropdownClick}
              />
            ) : (
              <CampaignItem
                key={`campaign-${item.uuid}`}
                campaign={item}
                userType={userType}
                hoveredCampaign={hoveredCampaign}
                onHover={onCampaignHover}
                onClick={onCampaignClick}
                onDateClick={onDateClick}
                onArchiveModalChange={onArchiveModalChange}
                isModalOrDropdownClick={isModalOrDropdownClick}
                monthIndex={monthIndex}
                year={year}
              />
            ),
          )}
        </Flex>
      </Box>
    </Box>
  );
}
